// Casting a spell (issue 1530; docs/MRCS/shadowrun-3.md §9 step 16): the form behind "Cast spell…" in the dice bag,
// only where the table's system names spellcasting (so a d20 table never sees it). From a spell row of the caster's
// sheet (its Force learned, and its numbers read from its entry: the target attribute, the drain code, the kind of
// damage) or one typed, the caster picks a target, the Force (at most the one learned), the damage level for a damaging
// spell, the casting skill (the sheet's row the system names, or its specialization) and the pool dice for the casting
// (at most the skill) and for the drain. The browser names all of that and nothing the server decides: the server reads
// the sheets, throws the casting, the resistance and the drain, and lands the drain on the caster.
import { useEffect, useState } from 'react';
import { CAST_LIMITS, magicDiceLines, magicRefusal, addsDice, drawsFor, poolCap, type SeenPool, type CastSpell } from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchStanding, fetchTests, type SheetSpellRow, type TestsAnswer } from '../../entries';
import { asksFor } from './asksFor';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';
import { MagicDiceRows, useMagicLines } from './MagicDiceParts';
import type { MagicLine } from '@dragons-table/vtt-shared';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null }
interface Fighting { tokens: string[]; pools?: Record<string, SeenPool> }

const TYPED = 'typed';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);

export function CastForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.spellcasting;
  const kinds = systemRules.damageKinds ?? [];
  const levels = view?.codes.levels ?? [];
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => (isDm ? true : o.mine));
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? '');
  const [whom, setWhom] = useState('');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [spell, setSpell] = useState(TYPED);
  const [force, setForce] = useState('');
  const [level, setLevel] = useState(levels[1]?.key ?? levels[0]?.key ?? '');
  const [skill, setSkill] = useState(TYPED);
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('4');
  const [castDice, setCastDice] = useState('0');
  const [drainDice, setDrainDice] = useState('0');
  const [tn, setTn] = useState('');
  const [extraLabel, setExtraLabel] = useState('');
  const [extraValue, setExtraValue] = useState('0');
  const [drainLabel, setDrainLabel] = useState('');
  const [drainValue, setDrainValue] = useState('0');
  const [drainLess, setDrainLess] = useState('0');
  const [willing, setWilling] = useState(false);
  // The caster's Magic and drain rating, when its sheet holds none (an NPC the DM plays): the server reads the sheet's first.
  const [typedMagic, setTypedMagic] = useState('');
  const [typedDrain, setTypedDrain] = useState('');
  // A spell typed in: its name, Force learned, target, drain code and damage.
  const [typedName, setTypedName] = useState('');
  const [typedLearned, setTypedLearned] = useState('4');
  const [typedTarget, setTypedTarget] = useState(view?.targets[0]?.key ?? '');
  const [typedDrainMod, setTypedDrainMod] = useState('0');
  const [typedDrainLevel, setTypedDrainLevel] = useState('');
  const [typedDamage, setTypedDamage] = useState('');

  const caster = tokens.find((o) => o.id === who);
  useEffect(() => {
    setAnswer(null); setSpell(TYPED); setSkill(TYPED);
    if (!caster?.character) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, who, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      if (a?.spells?.length) setSpell('s:0');
      // The casting skill the system names (its specialization first, when the sheet has one), else the first.
      const skills = (a?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
      const named = skills.map((c, i) => [c, i] as const).filter(([c]) => view?.skill && c.ref === view.skill);
      const best = named.find(([c]) => c.kind === 'specialization') ?? named[0];
      if (best) setSkill(String(best[1]));
      else if (skills.length) setSkill('0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, caster?.character, campaignId]);

  const got = answer && answer !== 'loading' ? answer : null;
  const row: SheetSpellRow | null = spell.startsWith('s:') ? got?.spells?.[Number(spell.slice(2))] ?? null : null;
  // A spirit's innate spell (issue 1172): Force and the casting skill both the spirit's Force, and no drain.
  const innate = row?.innate === true;
  const skills = innate && row ? [{ kind: 'skill' as const, name: 'Force', rating: row.force }] : (got?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
  useEffect(() => { if (innate) setSkill('0'); }, [innate]);
  // The target's own dice on its resistance (issue 1172: a power's dice on the system's resist test), read by the server
  // from its sheet for whoever may read it (the DM, its owner); each its own line, the DM's to switch off.
  const [resistAnswer, setResistAnswer] = useState<MagicLine[]>([]);
  const targetTok = tokens.find((o) => o.id === whom);
  useEffect(() => {
    setResistAnswer([]);
    if (!targetTok?.character || !view?.standingPowers || whom === who) return undefined;
    let live = true;
    void fetchStanding(campaignId, whom, scene?.id ?? null).then((a) => { if (live) setResistAnswer((a?.resist ?? []).map((term) => ({ term }))); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whom, who, targetTok?.character, campaignId]);
  useEffect(() => { setForce(String(row?.force ?? (int(typedLearned) ?? ''))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [spell, got]);
  // The spell as the wire names it: the row's entry, or the typed one.
  const typedL = int(typedLearned);
  const spellOf = (): CastSpell | null => {
    if (row) {
      if (!row.entry) return null;
      const e = row.entry;
      return { name: row.name, ...(row.ref ? { ref: row.ref } : {}), learned: row.force, target: e.target, drain: e.drain, ...(e.damage ? { damage: e.damage } : {}), ...(e.ranged ? { ranged: true as const } : {}), ...(e.category ? { category: e.category.slice(0, CAST_LIMITS.label) } : {}) };
    }
    if (!typedName.trim() || typedL === null || typedL < 1) return null;
    const dm = int(typedDrainMod) ?? 0;
    return {
      name: typedName.trim().slice(0, CAST_LIMITS.name), learned: Math.min(CAST_LIMITS.force, typedL),
      target: typedTarget === 'number' ? null : typedTarget ? { attribute: typedTarget } : null,
      drain: { modifier: Math.max(-10, Math.min(10, dm)), ...(typedDrainLevel ? { level: typedDrainLevel } : {}), levelModifier: 0 },
      ...(typedDamage ? { damage: typedDamage } : {}),
    };
  };
  const sp = spellOf();
  const forceN = int(force);
  const chosenSkill = skill === TYPED ? null : skills[Number(skill)] ?? null;
  const rating = chosenSkill ? chosenSkill.rating : int(typedRating);
  const skillName = chosenSkill ? chosenSkill.name : typedSkill.trim();
  const needsLevel = Boolean(sp && (sp.damage || sp.drain.level === undefined));
  const targetWord = sp?.target && 'attribute' in sp.target ? view?.targets.find((t) => t.key === (sp.target as { attribute: string }).attribute) ?? null : null;
  const needsTn = Boolean(sp && !targetWord && !(sp.target && 'number' in sp.target));
  // Pool dice from the system's spellcasting pool: the casting's at most the skill, the drain's any of what is left.
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const fighting = enc?.combatants?.find((c) => c.tokens.includes(who));
  const pool = (systemRules.pools ?? []).find((p) => p.key === view?.pool && addsDice(p) && drawsFor(p, 'spell') && fighting?.pools?.[p.key]) ?? null;
  const left = pool ? fighting!.pools![pool.key]!.left : 0;
  const cap = pool && rating ? poolCap(pool, 'spell', rating) : null;
  const castN = int(castDice) ?? 0; const drainN = int(drainDice) ?? 0;
  const castMost = Math.min(left, cap ?? left);
  const tnN = int(tn);
  const drainExtra = int(drainValue) ?? 0;
  const extra = int(extraValue) ?? 0;
  // The sheet's own dice (issue 1172): its totem's for the spell's category, its element's against the opposed one.
  const magicLines = view?.magicDice && got?.magic ? magicDiceLines(view.magicDice, got.magic, { for: 'spell', ...(row?.entry?.category ? { category: row.entry.category } : {}), combat: Boolean(fighting) }) : [];
  const magic = useMagicLines(magicLines);
  const resistLines = targetWord?.resists && !willing ? resistAnswer : [];
  const resistState = useMagicLines(resistLines);
  // What the caster's entry rules out (issue 1172: an elementalist's element), said before the cast.
  const refusal = view?.magicDice && got?.magic && row?.entry?.category && !innate ? magicRefusal(view.magicDice, got.magic, { for: 'spell', category: row.entry.category }) : null;
  const ready = Boolean(!refusal && view && caster && whom && sp && forceN && forceN >= 1 && forceN <= sp.learned && rating && rating >= 1 && skillName
    && (!needsLevel || level) && (!needsTn || (tnN && tnN >= 1)) && castN >= 0 && drainN >= 0 && castN <= castMost && castN + drainN <= left);

  if (!view) return null;
  if (!mine.length) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="cast-form">
        You have no token at the table to cast with.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  return (
    <form className="space-y-1 rounded border border-violet-800 p-2 text-xs" data-testid="cast-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !sp || !forceN || !rating) return;
      const term = (l: string, v: number) => (l.trim() && v ? [{ label: l.trim().slice(0, CAST_LIMITS.label), value: Math.max(-CAST_LIMITS.modifier, Math.min(CAST_LIMITS.modifier, v)) }] : []);
      const body = {
        kind: 'spell.cast', caster: who, target: whom, spell: sp, force: forceN, ...(needsLevel ? { level } : {}),
        skill: { name: skillName.slice(0, CAST_LIMITS.name), rating, ...(chosenSkill?.ref ? { ref: chosenSkill.ref } : {}) },
        ...(needsTn && tnN ? { tn: tnN } : !needsTn && tnN && isDm ? { tn: tnN } : {}),
        modifiers: [...term(extraLabel, extra), ...magic.tn].slice(0, CAST_LIMITS.modifiers), ...(magic.dice.length ? { dice: magic.dice } : {}), ...(isDm && resistLines.length ? { resistDice: resistState.dice } : {}), ...(innate ? { innate: true } : {}), drainModifiers: term(drainLabel, drainExtra),
        ...(pool && castN + drainN > 0 ? { pool: { key: pool.key, cast: castN, drain: drainN } } : {}),
        ...(int(drainLess) ? { drainLess: Math.max(0, Math.min(CAST_LIMITS.drainLess, int(drainLess)!)) } : {}),
        ...(willing ? { willing: true } : {}),
        ...(innate && row ? { magic: Math.min(CAST_LIMITS.magic, row.force) } : int(typedMagic) !== null && int(typedMagic)! >= 0 ? { magic: Math.min(CAST_LIMITS.magic, int(typedMagic)!) } : {}),
        ...(int(typedDrain) !== null && int(typedDrain)! >= 1 ? { drainRating: Math.min(CAST_LIMITS.rating, int(typedDrain)!) } : {}),
        private: isDm ? priv : false,
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That spell could not be cast.'); else onClose(); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Caster
          <select aria-label="Caster" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">at
          <select aria-label="Spell target" className={field} value={whom} onChange={(e) => setWhom(e.target.value)}>
            <option value="">Pick a target…</option>
            {tokens.map((o) => <option key={o.id} value={o.id}>{o.id === who ? `${o.name || 'Unnamed token'} (self)` : o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
      </div>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300">{got.notice}</p>}
      <label className="flex items-center gap-1">Spell
        <select aria-label="Spell" className={field} value={spell} onChange={(e) => setSpell(e.target.value)}>
          {(got?.spells ?? []).map((s, i) => <option key={`s:${i}`} value={`s:${i}`}>{s.innate ? `Innate Spell: ${s.name} (the spirit's Force ${s.force}, no drain)` : `${s.name} (Force ${s.force})${s.entry?.drainText ? ` · drain ${s.entry.drainText}` : ''}`}</option>)}
          <option value={TYPED}>A spell typed in…</option>
        </select>
      </label>
      {row && !row.entry && <p className="text-amber-300">That spell's entry can't be read just now: type the spell in.</p>}
      {spell === TYPED && (
        <div className="flex flex-wrap items-center gap-1" data-testid="cast-typed-spell">
          <input aria-label="Spell name" placeholder="Stunbolt" maxLength={CAST_LIMITS.name} className={`${field} w-24`} value={typedName} onChange={(e) => setTypedName(e.target.value)} />
          <label className="flex items-center gap-1">learned at
            <input aria-label="Force learned" type="number" min={1} max={CAST_LIMITS.force} className={`${field} w-12`} value={typedLearned} onChange={(e) => setTypedLearned(e.target.value)} />
          </label>
          <select aria-label="Spell's target number" className={field} value={typedTarget} onChange={(e) => setTypedTarget(e.target.value)}>
            {view.targets.map((t) => <option key={t.key} value={t.key}>vs {t.label}</option>)}
            <option value="number">a number typed</option>
          </select>
          <label className="flex items-center gap-1">drain
            <input aria-label="Drain modifier" type="number" min={-10} max={10} className={`${field} w-10`} value={typedDrainMod} onChange={(e) => setTypedDrainMod(e.target.value)} />
          </label>
          <select aria-label="Drain level" className={field} value={typedDrainLevel} onChange={(e) => setTypedDrainLevel(e.target.value)}>
            <option value="">(the damage level)</option>
            {levels.map((l) => <option key={l.key} value={l.key}>{l.abbr}</option>)}
          </select>
          <select aria-label="Spell damage" className={field} value={typedDamage} onChange={(e) => setTypedDamage(e.target.value)}>
            <option value="">No damage</option>
            {kinds.map((k) => <option key={k.key} value={k.key}>{k.label} damage</option>)}
          </select>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Force
          <input aria-label="Force" type="number" min={1} max={sp?.learned ?? CAST_LIMITS.force} className={`${field} w-12`} value={force} onChange={(e) => setForce(e.target.value)} />
        </label>
        {sp && <span className="text-stone-400">at most {sp.learned}</span>}
        {needsLevel && (
          <select aria-label="Damage level" className={field} value={level} onChange={(e) => setLevel(e.target.value)}>
            {levels.map((l) => <option key={l.key} value={l.key}>{l.abbr} ({l.label})</option>)}
          </select>
        )}
        <label className="flex items-center gap-1" title="A limited spell's Force for drain: fetish 1, exclusive 2, both 3, when the limit was taken off the drain.">drain Force less
          <input aria-label="Drain Force less" type="number" min={0} max={CAST_LIMITS.drainLess} className={`${field} w-10`} value={drainLess} onChange={(e) => setDrainLess(e.target.value)} />
        </label>
      </div>
      <label className="flex items-center gap-1">Skill
        <select aria-label="Casting skill" className={field} value={skill} onChange={(e) => setSkill(e.target.value)}>
          {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
          <option value={TYPED}>A rating typed in…</option>
        </select>
      </label>
      {skill === TYPED && (
        <div className="flex items-center gap-1">
          <input aria-label="Casting skill name" placeholder="Sorcery" maxLength={CAST_LIMITS.name} className={`${field} w-24`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
          <input aria-label="Casting skill rating" type="number" min={1} max={CAST_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1">
        {targetWord && <span className="text-stone-400" data-testid="cast-tn-from">TN: the target's {targetWord.label}{targetWord.resists ? `, which resists against the Force` : ''}</span>}
        {sp?.target && 'number' in sp.target && <span className="text-stone-400">TN {sp.target.number}</span>}
        {(needsTn || isDm) && (
          <label className="flex items-center gap-1" title={needsTn ? 'The spell names no number the table can read.' : 'Only when the target\'s sheet has none.'}>{needsTn ? 'TN' : 'TN, typed'}
            <input aria-label="Spell TN" type="number" min={1} max={CAST_LIMITS.target} className={`${field} w-12`} value={tn} onChange={(e) => setTn(e.target.value)} />
          </label>
        )}
        {targetWord?.resists && <label className="flex items-center gap-1"><input type="checkbox" aria-label="Willing target" checked={willing} onChange={(e) => setWilling(e.target.checked)} />willing (no resistance)</label>}
      </div>
      <MagicDiceRows lines={magicLines} state={magic} isDm={isDm} testid="cast-magic-dice" />
      {resistLines.length > 0 && <p className="text-stone-400">The target's resistance:</p>}
      <MagicDiceRows lines={resistLines} state={resistState} isDm={isDm} testid="cast-resist-dice" />
      {refusal && <p className="text-amber-300" data-testid="cast-refused">{refusal}</p>}
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-stone-400">Casting TN</span>
        <input aria-label="Other modifier label" placeholder="sustaining" maxLength={CAST_LIMITS.label} className={`${field} w-20`} value={extraLabel} onChange={(e) => setExtraLabel(e.target.value)} />
        <input aria-label="Other modifier to the TN" type="number" className={`${field} w-12`} value={extraValue} onChange={(e) => setExtraValue(e.target.value)} />
        <span className="text-stone-400">Drain TN</span>
        <input aria-label="Drain modifier label" placeholder="sustaining" maxLength={CAST_LIMITS.label} className={`${field} w-20`} value={drainLabel} onChange={(e) => setDrainLabel(e.target.value)} />
        <input aria-label="Other modifier to the drain TN" type="number" className={`${field} w-12`} value={drainValue} onChange={(e) => setDrainValue(e.target.value)} />
      </div>
      {asksFor(caster?.character, answer, 'cast.magic', 'cast.drain') && (
        <div className="flex flex-wrap items-center gap-1" data-testid="cast-typed-caster" title="Only when the caster's sheet has none.">
          <label className="flex items-center gap-1">{view.magic}
            <input aria-label="Caster's Magic" type="number" min={0} max={CAST_LIMITS.magic} className={`${field} w-12`} value={typedMagic} onChange={(e) => setTypedMagic(e.target.value)} />
          </label>
          <label className="flex items-center gap-1">drain rating
            <input aria-label="Caster's drain rating" type="number" min={1} max={CAST_LIMITS.rating} className={`${field} w-12`} value={typedDrain} onChange={(e) => setTypedDrain(e.target.value)} />
          </label>
        </div>
      )}
      {pool && (
        <div className="flex flex-wrap items-center gap-1" data-testid="cast-pool">
          <label className="flex items-center gap-1">{pool.label}: casting
            <input aria-label="Pool dice for the casting" type="number" min={0} max={castMost} className={`${field} w-12`} value={castDice} onChange={(e) => setCastDice(e.target.value)} />
          </label>
          <label className="flex items-center gap-1">drain
            <input aria-label="Pool dice for the drain" type="number" min={0} max={Math.max(0, left - castN)} className={`${field} w-12`} value={drainDice} onChange={(e) => setDrainDice(e.target.value)} />
          </label>
          <span className="text-stone-400">{left} left{cap !== null ? `; the casting at most ${cap}` : ''}</span>
        </div>
      )}
      {sp && forceN && <p data-testid="cast-summary">Force {forceN}{needsLevel && level ? ` · ${levels.find((l) => l.key === level)?.abbr ?? ''}` : ''}{rating ? ` · ${rating}${castN ? ` + ${castN}` : ''} dice` : ''} · drain TN {Math.max(view.minTarget, Math.floor(Math.max(1, forceN - (int(drainLess) ?? 0)) / view.divide) + sp.drain.modifier + (drainLabel.trim() ? drainExtra : 0))}</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-violet-700`} disabled={!ready}>Cast</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: players see only that a spell was cast.' : 'Public: the table sees it.'}</span>}
      </div>
    </form>
  );
}
